import { settingsAPI } from '../services/api';

export const ERP_STORE_CONFIG_EVENT = 'erp-store-config-loaded';

const DEFAULT_STORES = [];
const DEFAULT_PAYMENT_METHODS = [
  { code: 'cash', libelle: 'Espèces', parametres: { actif: true } },
  { code: 'card', libelle: 'Carte bancaire', parametres: { actif: true } },
  { code: 'pax_sg', libelle: 'PAX A920Pro / SG', parametres: { actif: true } },
  { code: 'check', libelle: 'Chèque', parametres: { actif: true } },
  { code: 'sumup', libelle: 'SumUp', parametres: { actif: true } },
  { code: 'installment', libelle: 'Mensualités', parametres: { actif: true, max_installments: 4 } },
];
const PAYMENT_METHOD_LABELS = {
  cash: 'Espèces',
  card: 'Carte bancaire',
  pax_sg: 'PAX A920Pro / SG',
  check: 'Chèque',
  sumup: 'SumUp',
  installment: 'Mensualités',
  transfer: 'Virement',
};

const STORE_THEME_SEQUENCE = [
  {
    subtle: 'bg-sky-100 text-sky-800',
    accent: 'bg-sky-100 text-sky-700',
    pill: 'badge-sky',
  },
  {
    subtle: 'bg-indigo-100 text-indigo-800',
    accent: 'bg-purple-100 text-purple-700',
    pill: 'badge-purple',
  },
  {
    subtle: 'bg-emerald-100 text-emerald-800',
    accent: 'bg-green-100 text-green-700',
    pill: 'badge-emerald',
  },
  {
    subtle: 'bg-amber-100 text-amber-800',
    accent: 'bg-amber-100 text-amber-700',
    pill: 'badge-yellow',
  },
  {
    subtle: 'bg-rose-100 text-rose-800',
    accent: 'bg-rose-100 text-rose-700',
    pill: 'badge-rose',
  },
];

const DOMICILE_LOCATION = {
  code: 'domicile',
  ville: 'À domicile',
  aliases: ['domicile', 'a domicile', 'home'],
  complement_adresse: '',
  code_postal: '',
  numero_de_tel: '',
  parametres: {},
  moyens_de_paiement: [],
};

let configuredStores = normalizeStores(DEFAULT_STORES);
let hasLoadedStoreConfig = false;
let storeConfigPromise = null;

function normalizeText(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

function normalizePaymentMethod(method, index) {
  const code = String(method?.code || '').trim();

  if (!code) {
    return null;
  }

  const rawParameters = method?.parametres && typeof method.parametres === 'object'
    ? method.parametres
    : {};

  return {
    code,
    libelle: String(method?.libelle || PAYMENT_METHOD_LABELS[code] || code).trim(),
    parametres: Object.fromEntries(
      Object.entries(rawParameters)
        .map(([key, value]) => [String(key || '').trim(), value])
        .filter(([key]) => Boolean(key))
    ),
    order: index,
  };
}

function normalizeBoolean(value, fallback = false) {
  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'string') {
    const normalizedValue = value.trim().toLowerCase();
    if (['true', '1', 'yes', 'oui', 'on'].includes(normalizedValue)) {
      return true;
    }
    if (['false', '0', 'no', 'non', 'off'].includes(normalizedValue)) {
      return false;
    }
  }

  if (typeof value === 'number') {
    return value !== 0;
  }

  return fallback;
}

function normalizeLocation(location, index, storeCode) {
  const code = String(location?.code || '').trim();
  const moyensDePaiement = Array.isArray(location?.moyens_de_paiement)
    ? location.moyens_de_paiement
      .map((method, paymentMethodIndex) => normalizePaymentMethod(method, paymentMethodIndex))
      .filter(Boolean)
    : [];

  if (!code) {
    return {
      code: `${storeCode || 'location'}_${index}`,
      ville: '',
      aliases: [],
      complement_adresse: '',
      code_postal: '',
      numero_de_tel: '',
      parametres: {},
      moyens_de_paiement: moyensDePaiement,
    };
  }

  return {
    code,
    ville: String(location?.ville || '').trim(),
    aliases: Array.isArray(location?.aliases)
      ? location.aliases.map((alias) => String(alias || '').trim()).filter(Boolean)
      : [],
    complement_adresse: String(location?.complement_adresse || '').trim(),
    code_postal: String(location?.code_postal || '').trim(),
    numero_de_tel: String(location?.numero_de_tel || '').trim(),
    parametres: Object.fromEntries(
      Object.entries(location?.parametres && typeof location.parametres === 'object' ? location.parametres : {})
        .map(([key, value]) => [String(key || '').trim(), value])
        .filter(([key]) => Boolean(key))
    ),
    moyens_de_paiement: moyensDePaiement,
  };
}

function normalizeStore(store, index) {
  const code = String(store?.code || '').trim();

  if (!code) {
    return {
      code: `store_${index}`,
      nom: '',
      email: '',
      siret: '',
      localites: [],
    };
  }

  const localites = Array.isArray(store?.localites)
    ? store.localites.map((location, locationIndex) => normalizeLocation(location, locationIndex, code))
    : [];

  return {
    code,
    nom: String(store?.nom || '').trim(),
    email: String(store?.email || '').trim(),
    siret: String(store?.siret || '').trim(),
    localites,
  };
}

function normalizeStores(stores) {
  const normalizedStores = Array.isArray(stores)
    ? stores.map((store, index) => normalizeStore(store, index)).filter((store) => store.code)
    : [];

  return normalizedStores;
}

function dispatchStoreConfigEvent() {
  if (typeof window === 'undefined') {
    return;
  }

  window.dispatchEvent(new CustomEvent(ERP_STORE_CONFIG_EVENT, {
    detail: configuredStores,
  }));
}

export function getConfiguredStores() {
  return configuredStores;
}

export function getDefaultStoreCode(fallback = '') {
  return configuredStores[0]?.code || fallback;
}

export function getStoreByCode(storeCode) {
  return configuredStores.find((store) => store.code === storeCode) || null;
}

export function getPrimaryLocationForStore(storeCode) {
  const directLocation = getAllLocations().find((location) => location.code === storeCode);
  if (directLocation) {
    return directLocation;
  }

  const store = getStoreByCode(storeCode);
  if (!store) {
    return null;
  }

  return (
    store.localites.find((location) => location.code === storeCode)
    || store.localites[0]
    || null
  );
}

export function getStoreLabel(storeCode, fallback = '') {
  const directLocation = getAllLocations({ includeDomicile: true }).find((location) => location.code === storeCode);
  if (directLocation) {
    return directLocation.ville || fallback || storeCode;
  }

  const store = getStoreByCode(storeCode);
  if (!store) {
    return fallback || storeCode;
  }

  const primaryLocation = getPrimaryLocationForStore(storeCode);
  return primaryLocation?.ville || store.nom || fallback || storeCode;
}

export function getStoreOptions({ includeAll = false, allLabel = 'Tout' } = {}) {
  const options = configuredStores.map((store) => ({
    value: store.code,
    label: store.nom || getStoreLabel(store.code, store.code),
  }));

  return includeAll
    ? [{ value: 'all', label: allLabel }, ...options]
    : options;
}

export function getStoreTheme(storeCode) {
  const storeIndex = configuredStores.findIndex((store) => store.code === storeCode);
  const theme = storeIndex >= 0
    ? STORE_THEME_SEQUENCE[storeIndex % STORE_THEME_SEQUENCE.length]
    : null;

  return theme || {
    subtle: 'bg-slate-100 text-slate-800',
    accent: 'bg-gray-100 text-gray-700',
    pill: 'badge-gray',
  };
}

export function getPrimaryLocationCodeForStore(storeCode, fallback = '') {
  return getPrimaryLocationForStore(storeCode)?.code || fallback;
}

export function getAllLocations({ includeDomicile = false } = {}) {
  const locations = configuredStores.flatMap((store) => store.localites || []);
  return includeDomicile ? [...locations, DOMICILE_LOCATION] : locations;
}

export function getLocationByCode(locationCode, { includeDomicile = true } = {}) {
  return getAllLocations({ includeDomicile }).find((location) => location.code === locationCode) || null;
}

export function getPaymentMethodLabel(paymentMethodCode, fallback = 'Paiement') {
  return PAYMENT_METHOD_LABELS[paymentMethodCode] || fallback || paymentMethodCode;
}

export function getPaymentMethodsForLocation(locationCode, { fallbackToDefaults = false } = {}) {
  const location = getLocationByCode(locationCode);
  const configuredMethods = Array.isArray(location?.moyens_de_paiement)
    ? location.moyens_de_paiement
    : [];

  if (configuredMethods.length > 0) {
    return configuredMethods;
  }

  return fallbackToDefaults ? DEFAULT_PAYMENT_METHODS : [];
}

export function getPaymentMethodsForStore(storeCode, { fallbackToDefaults = false } = {}) {
  const primaryLocationCode = getPrimaryLocationCodeForStore(storeCode);
  return getPaymentMethodsForLocation(primaryLocationCode, { fallbackToDefaults });
}

export function getEnabledPaymentMethodsForStore(storeCode, { fallbackToDefaults = false } = {}) {
  return getPaymentMethodsForStore(storeCode, { fallbackToDefaults })
    .filter((method) => normalizeBoolean(method?.parametres?.actif, false))
    .map((method) => ({
      ...method,
      libelle: method.libelle || getPaymentMethodLabel(method.code, method.code),
    }));
}

export function getPaymentMethodConfigForStore(storeCode, paymentMethodCode, { fallbackToDefaults = false } = {}) {
  return getPaymentMethodsForStore(storeCode, { fallbackToDefaults })
    .find((method) => method.code === paymentMethodCode) || null;
}

export function getDefaultPaymentMethodForStore(storeCode, fallback = 'cash', { fallbackToDefaults = false } = {}) {
  const primaryLocationCode = getPrimaryLocationCodeForStore(storeCode);
  const location = getLocationByCode(primaryLocationCode);
  const configuredDefault = String(location?.parametres?.default_payment_method || '').trim();
  const enabledMethods = getEnabledPaymentMethodsForStore(storeCode, { fallbackToDefaults });

  if (configuredDefault && enabledMethods.some((method) => method.code === configuredDefault)) {
    return configuredDefault;
  }

  return enabledMethods[0]?.code || fallback;
}

export function getInstallmentOptionsForStore(storeCode, { fallbackToDefaults = false } = {}) {
  const installmentMethod = getPaymentMethodConfigForStore(storeCode, 'installment', { fallbackToDefaults });
  const configuredMaxInstallments = Number(installmentMethod?.parametres?.max_installments);
  const maxInstallments = Number.isFinite(configuredMaxInstallments) && configuredMaxInstallments >= 2
    ? Math.floor(configuredMaxInstallments)
    : 4;

  return Array.from({ length: maxInstallments - 1 }, (_, index) => index + 2);
}

export function getLocationOptions({ includeAll = false, includeDomicile = false, allLabel = 'Toutes les localités' } = {}) {
  const options = getAllLocations({ includeDomicile }).map((location) => ({
    value: location.code,
    label: location.ville || location.code,
  }));

  return includeAll
    ? [{ value: 'all', label: allLabel }, ...options]
    : options;
}

export function getStoreCodeForLocation(locationCode) {
  const store = configuredStores.find((entry) => (
    Array.isArray(entry.localites) && entry.localites.some((location) => location.code === locationCode)
  ));

  return store?.code || '';
}

export function findMatchingLocationCode(value, fallback = '') {
  const normalizedValue = normalizeText(value);
  if (!normalizedValue) {
    return fallback;
  }

  const locations = getAllLocations({ includeDomicile: true });

  const exactMatch = locations.find((location) => {
    const candidates = [
      location.code,
      location.ville,
      location.code_postal,
      location.complement_adresse,
      ...(location.aliases || []),
    ];

    return candidates.some((candidate) => normalizeText(candidate) === normalizedValue);
  });

  if (exactMatch) {
    return exactMatch.code;
  }

  const partialMatch = locations.find((location) => {
    const candidates = [
      location.ville,
      location.code_postal,
      location.complement_adresse,
      ...(location.aliases || []),
    ];

    return candidates.some((candidate) => {
      const normalizedCandidate = normalizeText(candidate);
      return normalizedCandidate && normalizedValue.includes(normalizedCandidate);
    });
  });

  return partialMatch?.code || fallback;
}

export async function initializeStoreConfig({ forceRefresh = false } = {}) {
  if (hasLoadedStoreConfig && !forceRefresh) {
    return configuredStores;
  }

  if (storeConfigPromise) {
    return storeConfigPromise;
  }

  if (forceRefresh) {
    hasLoadedStoreConfig = false;
  }

  storeConfigPromise = settingsAPI.getStores()
    .then((response) => {
      configuredStores = normalizeStores(response?.data);
      hasLoadedStoreConfig = true;
      dispatchStoreConfigEvent();
      return configuredStores;
    })
    .catch((error) => {
      console.error('Error loading store configuration:', error);
      hasLoadedStoreConfig = true;
      dispatchStoreConfigEvent();
      return configuredStores;
    })
    .finally(() => {
      storeConfigPromise = null;
    });

  return storeConfigPromise;
}

export async function refreshStoreConfig() {
  return initializeStoreConfig({ forceRefresh: true });
}
